/**
 * MessageList.js - Chat Messages Display Component
 * Renders the list of chat messages with all message types and features
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Icons } from './Icons';
import { config } from '../config';
import { getGreeting } from '../utils/storage';

/**
 * Truncate text to a maximum length
 */
const truncateText = (text, maxLength = 200) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const LONG_RESPONSE_CHARS = 15000;

const downloadMarkdown = (text, idx) => {
  const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `response-${idx + 1}.md`;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Download a generated image (base64 or presigned URL) as a PNG file.
 */
const downloadGeneratedImage = (msg, idx) => {
  const filename = msg.generated_image_filename || `generated-image-${idx + 1}.png`;

  if (msg.generated_image_data) {
    // Convert base64 to Blob for reliable download across browsers
    const byteChars = atob(msg.generated_image_data);
    const byteArr = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteArr[i] = byteChars.charCodeAt(i);
    }
    const blob = new Blob([byteArr], { type: 'image/png' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  } else if (msg.generated_image_url) {
    // Presigned S3 URL — open in new tab (same-origin download not possible)
    const a = document.createElement('a');
    a.href = msg.generated_image_url;
    a.download = filename;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
  }
};

/**
 * GeneratedImageCard — displays a model-generated image with a download button.
 * Shown in assistant messages produced by image generation models (e.g. TAMC 4.5 image).
 */
const GeneratedImageCard = ({ msg, idx }) => {
  const imgSrc = msg.generated_image_data
    ? `data:image/png;base64,${msg.generated_image_data}`
    : msg.generated_image_url || null;

  if (!imgSrc) return null;

  return (
    <div className="generated-image-card">
      <img
        src={imgSrc}
        alt="Generated infographic"
        className="generated-image"
      />
      <div className="generated-image-actions">
        <button
          className="generated-image-download-btn"
          onClick={() => downloadGeneratedImage(msg, idx)}
          title="Download PNG"
        >
          <Icons.Download />
          <span>Download PNG</span>
        </button>
      </div>
    </div>
  );
};

const AviationArtifactActions = ({ msg, onRetry, onDownloadPptx }) => {
  const status = msg.aviationArtifactStatus;
  if (!status) return null;

  return (
    <div className="generated-image-card aviation-artifact-actions">
      <div className="generated-image-actions">
        {msg.aviationPptxAvailable && (
          <button
            className="generated-image-download-btn"
            onClick={() => onDownloadPptx && onDownloadPptx(msg)}
            title="Download combined PowerPoint"
          >
            <Icons.Download />
            <span>Download PPTX</span>
          </button>
        )}
        {status === 'generating' && (
          <span>Creating infographic and PowerPoint…</span>
        )}
        {status === 'failed' && (
          <>
            <span>{msg.aviationArtifactError || 'Infographic and PPTX generation failed.'}</span>
            <button
              className="generated-image-download-btn"
              onClick={() => onRetry && onRetry(msg)}
              title="Retry infographic and PowerPoint"
            >
              <span>Retry infographic &amp; PPTX</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

/**
 * Get the best thumbnail from face recognition sources
 * Priority: cropped_face (from original image) > source thumbnails (from FaceCheck)
 */
const getFaceThumbnail = (face) => {
  // First priority: cropped face from the original uploaded image
  if (face.cropped_face) {
    return `data:image/jpeg;base64,${face.cropped_face}`;
  }
  
  // Second priority: thumbnail from FaceCheck sources
  if (face.sources && face.sources.length > 0) {
    const sourceWithThumb = face.sources.find(s => s.thumbnail);
    if (sourceWithThumb?.thumbnail) {
      return `data:image/jpeg;base64,${sourceWithThumb.thumbnail}`;
    }
  }
  
  return null;
};

/**
 * Get the display name for the recognition source
 */
const getRecognitionSourceLabel = (source) => {
  if (source === 'aws_rekognition') return 'AWS Rekognition';
  if (source === 'facecheck') return 'FaceCheck.id';
  return source || 'Unknown';
};

/**
 * Get confidence level label and description
 */
const getConfidenceLevelInfo = (level) => {
  switch (level) {
    case 'high':
      return { label: 'High Confidence', icon: '✓', description: 'Multiple authoritative sources agree' };
    case 'medium':
      return { label: 'Medium Confidence', icon: '~', description: 'Some supporting sources found' };
    case 'low':
      return { label: 'Low Confidence', icon: '?', description: 'Limited source verification' };
    default:
      return { label: 'Unknown', icon: '?', description: '' };
  }
};

/**
 * VideoScoreProgressCard - Live status while video dictionary scoring runs
 */
const VideoScoreProgressCard = ({ msg }) => {
  if (!msg.usedVideoScore || !msg.videoScoreInProgress) return null;

  return (
    <div className="video-score-progress-card">
      <div className="video-score-progress-header">
        <Icons.Video />
        <span>Video Dictionary Score in progress</span>
        <span className="video-score-progress-spinner" aria-hidden="true" />
      </div>
      <ul className="video-score-progress-steps">
        <li>Download video from URL</li>
        <li>Transcribe and summarize</li>
        <li>Score against Carinae dictionaries</li>
        <li>Generate score explanations</li>
      </ul>
      {msg.text && (
        <p className="video-score-progress-message">{msg.text}</p>
      )}
    </div>
  );
};

/**
 * DeepResearchProgressCard - Live status while a deep research job runs
 */
const DeepResearchProgressCard = ({ msg }) => {
  const isRunning = ['pending', 'running'].includes(msg.deepResearchJobStatus);
  const isSequoia = Boolean(msg.usedSequoiaStructure);
  if ((!msg.usedDeepResearch && !isSequoia) || !isRunning) return null;

  return (
    <div className="deep-research-progress-card">
      <div className="deep-research-progress-header">
        <Icons.Search />
        <span>{isSequoia ? 'Rewriting report' : 'Deep Research in progress'}</span>
      </div>
      {msg.deepResearchJobStep && (
        <div className="deep-research-progress-step">Stage: {msg.deepResearchJobStep}</div>
      )}
      <pre className="deep-research-progress-message">{msg.text}</pre>
    </div>
  );
};

const sourceTypeLabel = (sourceType) => {
  const normalized = (sourceType || 'web').toLowerCase();
  if (normalized.includes('communication')) return 'Communications';
  if (normalized.includes('document') || normalized.includes('attachment')) return 'Document';
  return 'Web';
};

const hostnameFromUrl = (url) => {
  if (!url) return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

const reportHasSourcesSection = (text) => {
  const lowered = (text || '').toLowerCase();
  return lowered.includes('## sources') || lowered.includes('## references');
};

/**
 * DeepResearchCitations - Numbered source cards (web-search-style references)
 */
const DeepResearchCitations = ({ citations }) => {
  const items = (citations || []).filter(
    (c) => c && (c.url || c.title || c.snippet)
  );
  if (!items.length) return null;

  return (
    <div className="deep-research-citations">
      <div className="deep-research-citations-title">
        <Icons.CheckCircle />
        <span>Sources ({items.length})</span>
      </div>
      <ol className="deep-research-citations-list">
        {items.map((citation, idx) => {
          const number = citation.index || idx + 1;
          const host = hostnameFromUrl(citation.url);
          const label = citation.title || host || `Source ${number}`;
          return (
            <li
              key={citation.chunk_id || `${number}-${label}`}
              className="deep-research-citation-card"
            >
              <div className="deep-research-citation-header">
                <span className="deep-research-citation-index">[{number}]</span>
                <span className="deep-research-citation-type">
                  {sourceTypeLabel(citation.source_type)}
                </span>
                {citation.verified === false && (
                  <span className="deep-research-citation-unverified">Unverified</span>
                )}
              </div>
              <div className="deep-research-citation-label">
                {citation.url ? (
                  <a href={citation.url} target="_blank" rel="noopener noreferrer">
                    <span>{label}</span>
                    <span className="deep-research-citation-external" aria-hidden="true">
                      ↗
                    </span>
                  </a>
                ) : (
                  <span>{label}</span>
                )}
              </div>
              {host && citation.title && (
                <div className="deep-research-citation-host">{host}</div>
              )}
              {citation.snippet && (
                <div className="deep-research-citation-snippet" title={citation.snippet}>
                  {truncateText(citation.snippet, 180)}
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

/**
 * CanvasLinkCard - Displays a clickable link to reopen a dictionary canvas
 */
const CanvasLinkCard = ({ msg, onClick }) => {
  const statusConfig = {
    pending: { label: 'Pending', className: 'status-pending' },
    analyzed: { label: 'Analyzed', className: 'status-analyzed' },
    approved: { label: 'Approved', className: 'status-approved' }
  };
  
  const status = statusConfig[msg.status] || statusConfig.pending;
  const displayName = msg.poiName || 'Dictionary Analysis';
  
  return (
    <div className="canvas-link-card" onClick={onClick}>
      <div className="canvas-link-content">
        {msg.imageUrl && (
          <img 
            src={msg.imageUrl} 
            alt="Analysis thumbnail" 
            className="canvas-link-thumbnail"
          />
        )}
        <div className="canvas-link-info">
          <div className="canvas-link-header">
            <Icons.Dictionary />
            <span className="canvas-link-title">{displayName}</span>
          </div>
          <div className="canvas-link-meta">
            <span className={`canvas-link-status ${status.className}`}>
              {msg.status === 'approved' && <Icons.CheckCircle />}
              {status.label}
            </span>
            {msg.createdAt && (
              <span className="canvas-link-date">
                {new Date(msg.createdAt).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
        <div className="canvas-link-action">
          <Icons.ChevronRight />
        </div>
      </div>
    </div>
  );
};

/**
 * FaceCard - Displays a single face recognition result
 */
const FaceCard = ({ face }) => {
  const thumbnail = getFaceThumbnail(face);
  const confidence = Math.round(face.confidence || 0);
  const name = face.name || 'Unknown Person';
  const recognitionSource = getRecognitionSourceLabel(face.recognition_source);
  const sources = face.sources || [];
  const sourceCount = face.source_count || sources.length;
  const confidenceLevel = face.confidence_level || 'low';
  const confidenceLevelInfo = getConfidenceLevelInfo(confidenceLevel);
  const isEnriched = face.enriched || face.recognition_source === 'facecheck_enriched';
  
  // Get first valid URL from sources (prefer high-authority sources)
  const primarySource = sources.find(s => (s.url || s.source_url) && (s.url || s.source_url).length > 0);
  const sourceUrl = primarySource?.url || primarySource?.source_url;
  
  return (
    <div className={`face-card confidence-${confidenceLevel} ${isEnriched ? 'enriched' : ''}`}>
      <div className="face-header">
        {thumbnail ? (
          <img 
            src={thumbnail} 
            alt={name} 
            className="face-thumbnail"
          />
        ) : (
          <div className="face-thumbnail-placeholder">
            <Icons.User />
          </div>
        )}
        <div className="face-info">
          <div className="face-name-row">
            <span className="face-name">{name}</span>
            <span 
              className={`confidence-level-badge ${confidenceLevel}`}
              title={confidenceLevelInfo.description}
            >
              {confidenceLevelInfo.icon}
            </span>
          </div>
          <div className="face-meta">
            <span className={`recognition-source ${face.recognition_source || 'unknown'}`}>
              {recognitionSource}
            </span>
            {sourceCount > 1 && (
              <span className="source-count" title={`Found in ${sourceCount} different sources`}>
                {sourceCount} sources
              </span>
            )}
          </div>
          {/* Enriched profile data from identity crawler */}
          {isEnriched && (
            <div className="face-profile">
              {face.occupation && (
                <div className="profile-field">
                  <span className="profile-label">Role:</span>
                  <span className="profile-value">{face.occupation}</span>
                </div>
              )}
              {face.organization && (
                <div className="profile-field">
                  <span className="profile-label">Organization:</span>
                  <span className="profile-value">{face.organization}</span>
                </div>
              )}
              {face.nationality && (
                <div className="profile-field">
                  <span className="profile-label">Nationality:</span>
                  <span className="profile-value">{face.nationality}</span>
                </div>
              )}
              {face.bio && (
                <div className="profile-bio">
                  {truncateText(face.bio, 150)}
                </div>
              )}
              {face.notable_facts && face.notable_facts.length > 0 && (
                <div className="profile-facts">
                  {face.notable_facts.slice(0, 2).map((fact, idx) => (
                    <div key={idx} className="profile-fact">• {truncateText(fact, 100)}</div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="face-confidence">
            <span className="confidence-value">{confidence}%</span>
            <div className="confidence-bar">
              <div 
                className={`confidence-fill ${confidenceLevel}`}
                style={{ width: `${confidence}%` }}
              />
            </div>
          </div>
        </div>
      </div>
      {sourceUrl && (
        <a 
          href={sourceUrl} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="face-link"
        >
          View Source ({primarySource?.source || (() => { try { return new URL(sourceUrl).hostname; } catch { return 'Link'; } })()})
        </a>
      )}
    </div>
  );
};

function MessageList({
  messages,
  user,
  isLoading,
  copiedMessageIdx,
  expandedCommunications,
  onCopyMessage,
  onToggleCommunications,
  onReopenCanvas,
  onRetryAviationArtifacts,
  onDownloadAviationPptx,
  messagesEndRef,
}) {
  // Welcome screen when no messages
  if (messages.length === 0) {
    return (
      <div className="welcome-screen">
        <h2>{getGreeting()}, {user.name?.split(' ')[0] || 'User'}!</h2>
        <p>Start a conversation with the AI intelligent assistant.<br />Your chats are private and secure.</p>
        <div className="welcome-badges">
          <span className="welcome-badge">Secure</span>
          {config.enableWebSearch && (
            <span className="welcome-badge">Web Search Enabled</span>
          )}
          <span className="welcome-badge">Data Protected</span>
        </div>
      </div>
    );
  }

  return (
    <div className="messages-wrapper">
      {messages.map((msg, idx) => {
        const isDeepResearchRunning =
          (msg.usedDeepResearch || msg.usedSequoiaStructure)
          && ['pending', 'running'].includes(msg.deepResearchJobStatus);
        const isVideoScoreRunning = Boolean(msg.videoScoreInProgress);

        // Handle dictionary canvas link messages
        if (msg.type === 'dictionary_canvas') {
          return (
            <div key={idx} className="message dictionary-canvas-message">
              <CanvasLinkCard 
                msg={msg} 
                onClick={() => onReopenCanvas && onReopenCanvas(msg)} 
              />
            </div>
          );
        }
        
        return (
        <div key={idx} className={`message ${msg.type}`}>
          <div className="message-inner">
            <div className={`message-avatar ${msg.type === 'assistant' ? 'bot' : ''} ${msg.type === 'error' ? 'error-icon' : ''}`}>
              {msg.type === 'user' ? (
                user.picture ? (
                  <img src={user.picture} alt={user.name} />
                ) : (
                  <span>{user.name?.charAt(0)?.toUpperCase() || '?'}</span>
                )
              ) : msg.type === 'assistant' ? (
                <Icons.Bot />
              ) : (
                <span>!</span>
              )}
            </div>
            <div className="message-content">
              {msg.type === 'assistant' && msg.usedVideoScore && !msg.videoScoreInProgress && (
                <div className="web-search-badge video-score-badge">
                  {typeof msg.videoScore === 'number'
                    ? `Dictionary Score: ${msg.videoScore}`
                    : 'Video Dictionary Score'}
                </div>
              )}
              {msg.type === 'assistant' && msg.usedSequoiaStructure && (
                <div className="web-search-badge">
                  Sequoia Structure
                </div>
              )}
              {msg.type === 'assistant' && msg.usedDeepResearch && !msg.usedSequoiaStructure && (
                <div className="web-search-badge deep-research-badge">
                  Deep Research Report
                </div>
              )}
              {msg.type === 'assistant' && msg.usedWebSearch && (
                <div className="web-search-badge">
                  Web Search Used
                </div>
              )}
              {msg.type === 'assistant' && msg.searchStatus === 'failed' && !msg.usedWebSearch && (
                <div className="web-search-badge web-search-failed">
                  Web Search Failed
                </div>
              )}
              {msg.type === 'assistant' && (msg.communicationSearchStatus === 'failed' || msg.communicationSearchStatus === 'error') && !msg.usedCommunicationSearch && (
                <div className="web-search-badge web-search-failed">
                  {msg.communicationSearchError === 'auth_error'
                    ? 'Communication Search Failed (database auth)'
                    : msg.communicationSearchError === 'connection_error'
                      ? 'Communication Search Failed (database connection)'
                      : msg.communicationSearchError === 'not_configured'
                        ? 'Communication Search Failed (not configured)'
                        : 'Communication Search Failed'}
                </div>
              )}
              <DeepResearchProgressCard msg={msg} />
              <VideoScoreProgressCard msg={msg} />
              {!isDeepResearchRunning && !isVideoScoreRunning && msg.type === 'assistant' ? (
                msg.text && msg.text.length > LONG_RESPONSE_CHARS ? (
                  <div className="long-response-notice">
                    <Icons.Download />
                    <span>Response is too long to display inline ({Math.round(msg.text.length / 1000)}k chars).</span>
                    <button
                      className="copy-btn download-md-btn"
                      onClick={() => downloadMarkdown(msg.text, idx)}
                    >
                      Download as Markdown
                    </button>
                  </div>
                ) : (
                  <div className="markdown-content">
                    <ReactMarkdown
                      components={{
                        a: ({ node, ...props }) => (
                          <a {...props} target="_blank" rel="noopener noreferrer" />
                        ),
                      }}
                    >
                      {msg.text}
                    </ReactMarkdown>
                  </div>
                )
              ) : !isDeepResearchRunning && !isVideoScoreRunning ? (
                <p>{msg.text}</p>
              ) : null}
              {msg.type === 'assistant' &&
                msg.deepResearchJobStatus === 'ready' &&
                !reportHasSourcesSection(msg.text) && (
                <DeepResearchCitations citations={msg.deepResearchCitations} />
              )}
              {/* Statistical Summary report links — rendered as real <a> elements
                  because ReactMarkdown sanitises blob: URLs */}
              {msg.type === 'assistant' && msg.reportLinks && msg.reportLinks.length > 0 && (
                <div className="statistical-report-links">
                  <p className="statistical-report-links-title">
                    Reports <span style={{ fontWeight: 'normal', fontSize: '0.85em', color: '#888' }}>(click to open in a new tab)</span>:
                  </p>
                  <ul className="statistical-report-links-list">
                    {msg.reportLinks.map((link, i) => (
                      <li key={i}>
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="statistical-report-link"
                        >
                          📄 {link.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Image display - prefer presigned URL, fallback to base64 */}
              {(msg.image_url || msg.image_data) && (
                <img 
                  src={msg.image_url || `data:image/jpeg;base64,${msg.image_data}`} 
                  alt="User provided" 
                  className="chat-image" 
                />
              )}
              {/* PDF display - with download link if URL available */}
              {(msg.pdf_url || msg.pdf_data) && (
                <div className="chat-pdf">
                  <Icons.Pdf />
                  <span>{msg.pdf_name || 'PDF Document'}</span>
                  {msg.pdf_url && (
                    <a 
                      href={msg.pdf_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="attachment-download"
                      title="Download PDF"
                    >
                      <Icons.Download />
                    </a>
                  )}
                </div>
              )}
              {/* Excel display - with download link if URL available */}
              {(msg.excel_url || msg.excel_data) && (
                <div className="chat-excel">
                  <Icons.Excel />
                  <span>{msg.excel_name || 'Excel Spreadsheet'}</span>
                  {msg.excel_url && (
                    <a 
                      href={msg.excel_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="attachment-download"
                      title="Download Excel"
                    >
                      <Icons.Download />
                    </a>
                  )}
                </div>
              )}
              {msg.usedWebSearch && msg.searchQuery && (
                <div className="search-query-info">
                  Searched for: <em>{msg.searchQuery}</em>
                </div>
              )}
              {msg.type === 'assistant' && msg.communications && msg.communications.length > 0 && (
                <div className="communications-section">
                  <button
                    className={`communications-toggle ${expandedCommunications[idx] ? 'expanded' : ''}`}
                    onClick={() => onToggleCommunications(idx)}
                  >
                    <Icons.ChevronDown />
                    <span>View {msg.communications.length} Related Communication{msg.communications.length !== 1 ? 's' : ''}</span>
                  </button>
                  {expandedCommunications[idx] && (
                    <div className="communications-list">
                      {msg.communications.map((comm, commIdx) => (
                        <div key={commIdx} className="communication-card">
                          <div className="communication-header">
                            {comm.timestamp && <span className="comm-timestamp">{comm.timestamp}</span>}
                            {comm.source && <span className="comm-source">{comm.source}</span>}
                            {comm.type && <span className="comm-type">{comm.type}</span>}
                          </div>
                          {comm.poster && <div className="comm-poster">By: {comm.poster}</div>}
                          <div className="comm-content">
                            {truncateText(comm.communication || comm.content)}
                          </div>
                          {comm.link && (
                            <a 
                              href={comm.link} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              className="comm-link"
                            >
                              View Source
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {msg.type === 'assistant' && msg.placeRecognitionResults && msg.placeRecognitionResults.length > 0 && (
                <div className="places-section">
                  <div className="places-badge">
                    <Icons.MapPin />
                    <span>{msg.placesFound} Place{msg.placesFound !== 1 ? 's' : ''} Identified</span>
                  </div>
                  <div className="places-list">
                    {msg.placeRecognitionResults.slice(0, 5).map((place, placeIdx) => (
                      <div key={placeIdx} className="place-card">
                        <div className="place-header">
                          {place.thumbnail && (
                            <img 
                              src={place.thumbnail} 
                              alt={place.title} 
                              className="place-thumbnail"
                            />
                          )}
                          <div className="place-info">
                            <span className="place-title">{place.title}</span>
                            {place.source && <span className="place-source">{place.source}</span>}
                            {place.subtitle && <span className="place-subtitle">{place.subtitle}</span>}
                          </div>
                        </div>
                        {place.link && (
                          <a 
                            href={place.link} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="place-link"
                          >
                            Learn More
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {msg.type === 'assistant' && msg.faceRecognitionResults && msg.faceRecognitionResults.length > 0 && (
                <div className="faces-section">
                  <div className="faces-badge">
                    <Icons.User />
                    <span>{msg.facesFound} Face{msg.facesFound !== 1 ? 's' : ''} Identified</span>
                  </div>
                  <div className="faces-list">
                    {msg.faceRecognitionResults.slice(0, 5).map((face, faceIdx) => (
                      <FaceCard key={faceIdx} face={face} />
                    ))}
                  </div>
                </div>
              )}
              {msg.usedVideoTranscription && (
                <div className="video-transcription-result">
                  <div className="video-transcription-badge">
                    <Icons.Video />
                    <span>Video Transcription</span>
                  </div>
                  <div className="video-transcription-meta">
                    {msg.videoFileName && (
                      <span>Source: {msg.videoFileName}</span>
                    )}
                    {msg.detectedLanguage && (
                      <span>Language: {msg.detectedLanguage}</span>
                    )}
                    {msg.translationApplied && (
                      <span>Translated to English</span>
                    )}
                  </div>
                </div>
              )}
              {msg.usedDeepfakeDetection && (
                <div className="deepfake-result">
                  <div className={`deepfake-verdict-badge deepfake-verdict-${(msg.deepfakeVerdict || 'INCONCLUSIVE').toLowerCase()}`}>
                    <Icons.DeepfakeScan />
                    <span>{msg.deepfakeVerdict || 'INCONCLUSIVE'}</span>
                  </div>
                  <div className="deepfake-stats">
                    <div className="deepfake-confidence">
                      <span className="deepfake-stat-label">Fake Confidence</span>
                      <div className="deepfake-confidence-bar">
                        <div
                          className="deepfake-confidence-fill"
                          style={{ width: `${Math.round((msg.deepfakeConfidence || 0) * 100)}%` }}
                        />
                      </div>
                      <span className="deepfake-stat-value">{Math.round((msg.deepfakeConfidence || 0) * 100)}%</span>
                    </div>
                    {msg.deepfakeFrameCount != null && msg.deepfakeFrameCount > 1 && (
                      <div className="deepfake-frames">
                        <span className="deepfake-stat-label">Frames analyzed</span>
                        <span className="deepfake-stat-value">
                          {msg.deepfakeFlaggedCount || 0} / {msg.deepfakeFrameCount} flagged
                        </span>
                      </div>
                    )}
                    {msg.deepfakeFrameCount === 1 && (
                      <div className="deepfake-frames">
                        <span className="deepfake-stat-label">Type</span>
                        <span className="deepfake-stat-value">Single image analysis</span>
                      </div>
                    )}
                    {msg.deepfakeFileName && (
                      <div className="deepfake-source">
                        <span className="deepfake-stat-label">Source</span>
                        <span className="deepfake-stat-value deepfake-file-name">{msg.deepfakeFileName}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* Generated image — shown for image-generation model responses */}
              {msg.type === 'assistant' && (msg.generated_image_data || msg.generated_image_url) && (
                <GeneratedImageCard msg={msg} idx={idx} />
              )}
              {msg.type === 'assistant' && msg.aviationArtifactStatus && (
                <AviationArtifactActions
                  msg={msg}
                  onRetry={onRetryAviationArtifacts}
                  onDownloadPptx={onDownloadAviationPptx}
                />
              )}
              {msg.type === 'assistant' && (
                <button
                  className={`copy-btn ${copiedMessageIdx === idx ? 'copied' : ''}`}
                  onClick={() => onCopyMessage(msg.text, idx, msg.reportHtml)}
                  title={
                    copiedMessageIdx === idx
                      ? 'Copied!'
                      : msg.reportHtml
                      ? 'Copy for Google Docs (Verdana 11pt)'
                      : 'Copy message'
                  }
                >
                  {copiedMessageIdx === idx ? <Icons.Check /> : <Icons.Copy />}
                  <span className="copy-btn-text">
                    {copiedMessageIdx === idx ? 'Copied!' : 'Copy'}
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
        );
      })}
      
      {isLoading && (
        <div className="message loading">
          <div className="message-inner">
            <div className="message-avatar bot">
              <Icons.Bot />
            </div>
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div ref={messagesEndRef} />
    </div>
  );
}

export default MessageList;

